import React from 'react'
import { Card, CardContent, CardMedia, Typography, Chip, Box, Grid, Avatar, Divider, IconButton, Collapse } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'

interface BlogListComponentProps {
    mode: 'list' | 'delete';
    _id: string;
    blogContent: string;
    blogCreatedAt: number;
    blogImage: string;
    blogReadCount: number;
    blogTitle: string;
    blogTrimester: string;
    blogUpdatedAt: number;
    isDeleted: boolean;
    isPriority: boolean;
    recipeReferences: [];
    relatedData: [];
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedBlogTitle?: string;
    setSelectedBlogTitle?: (selectedBlogTitle: string) => void;
    selectedBlogId?: string;
    setSelectedBlogId?: (selectedBlogId: string) => void;
}

export default function BlogListComponent({
    mode,
    _id,
    blogContent,
    blogCreatedAt,
    blogImage,
    blogReadCount,
    blogTitle,
    blogTrimester,
    blogUpdatedAt,
    isDeleted,
    isPriority,
    recipeReferences,
    relatedData,
    open,
    setOpen,
    selectedBlogTitle,
    setSelectedBlogTitle,
    selectedBlogId,
    setSelectedBlogId,
}: BlogListComponentProps) {
    const [expanded, setExpanded] = React.useState(false);

    const returnDate = (date: number) => {
        const newDate = new Date(date * 1000);
        return newDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleOpen = () => {
        setSelectedBlogTitle!(blogTitle);
        setSelectedBlogId!(_id);
        setOpen(true)
    };

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    // Check if blog content contains HTML
    const isHtmlContent = /<[^>]*>/g.test(blogContent);

    // Function to render blog content (HTML or plain text)
    const renderBlogContent = (content: string, truncate: boolean = false) => {
        if (isHtmlContent) {
            const contentToRender = truncate && content.length > 200 
                ? content.substring(0, 200) + '...' 
                : content;
            return <div dangerouslySetInnerHTML={{ __html: contentToRender }} />;
        }
        const textContent = truncate && content.length > 200 
            ? `${content.substring(0, 200)}...` 
            : content;
        return <span>{textContent}</span>;
    };

    return (
        <Card
            sx={{
                minWidth: 800,
                width: 800,
                minHeight: 600,
                margin: '16px auto',
                boxShadow: isDeleted ? '0 4px 20px rgba(239, 68, 68, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
                border: isDeleted ? '2px solid #ef4444' : isPriority ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                cursor: mode === 'delete' ? 'pointer' : 'default'
            }}
            onClick={mode === 'delete' ? handleOpen : undefined}
        >
            {/* Main Image */}
            <CardMedia sx={{ height: 300, position: 'relative' }}>
                <LazyLoadImage
                    src={blogImage}
                    alt={blogTitle}
                    effect="blur"
                    style={{
                        width: '500px',
                        height: '300px',
                    }}
                />

                {/* Status Badges */}
                <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, flexDirection: 'column' }}>
                    {isPriority && (
                        <Chip
                            label="Priority"
                            size="small"
                            sx={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    {isDeleted && (
                        <Chip
                            label="Deleted"
                            size="small"
                            sx={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    <Chip
                        label={`Trimester ${blogTrimester}`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(59, 130, 246, 0.9)',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                </Box>
            </CardMedia>

            <CardContent sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                {/* Main Content Area */}
                <Box sx={{ flex: 1 }}>
                    {/* Title and Meta Info */}
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: isDeleted ? '#ef4444' : 'inherit' }}>
                        {blogTitle}
                    </Typography>

                    {/* Stats Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {blogReadCount} views
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {returnDate(blogCreatedAt)}
                            </Typography>
                        </Box>
                        {blogUpdatedAt !== blogCreatedAt && (
                            <Typography variant="body2" color="text.secondary">
                                Updated: {returnDate(blogUpdatedAt)}
                            </Typography>
                        )}
                    </Box>

                    {/* Content Preview */}
                    <Typography variant="body1" sx={{
                        mb: 2,
                        lineHeight: 1.6,
                        minHeight: '80px',
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {renderBlogContent(blogContent, true)}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Related Data (New Recipe Format) */}
                    <Box sx={{ mb: 2, minHeight: '60px' }}>
                        {relatedData && (relatedData as any[]).length > 0 ? (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <RestaurantIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        Related Recipes ({(relatedData as any[]).length})
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {(relatedData as any[]).slice(0, 3).map((recipe: any, index: number) => (
                                        <Box key={index} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            backgroundColor: '#f8fafc',
                                            padding: '8px 12px',
                                            borderRadius: '20px',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <Avatar
                                                src={recipe.recipeImage}
                                                sx={{ width: 24, height: 24 }}
                                            />
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: '500' }}>
                                                {recipe.recipeName}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {(relatedData as any[]).length > 3 && (
                                        <Chip
                                            label={`+${(relatedData as any[]).length - 3} more`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No related recipes
                            </Typography>
                        )}
                    </Box>

                    {/* Recipe References (Old Format) */}
                    {recipeReferences && (recipeReferences as any[]).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocalOfferIcon fontSize="small" color="secondary" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    Recipe References ({(recipeReferences as any[]).length})
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(recipeReferences as any[]).map((recipe: any, index: number) => (
                                    <Chip
                                        key={index}
                                        label={recipe.recipeTitle || recipe}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Bottom Section - Expand Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <IconButton
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more"
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Additional Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Blog ID:</strong> {_id}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Trimester:</strong> {blogTrimester}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Status:</strong> {isDeleted ? 'Deleted' : 'Active'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Priority:</strong> {isPriority ? 'High' : 'Normal'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Read Count:</strong> {blogReadCount}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Last Updated:</strong> {returnDate(blogUpdatedAt)}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Full Content */}
                        {(blogContent.length > 200 || isHtmlContent) && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Full Content
                                </Typography>
                                <Box sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.6,
                                    backgroundColor: '#f8fafc',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {renderBlogContent(blogContent, false)}
                                </Box>
                            </Box>
                        )}

                        {/* All Related Recipes (Expanded View) */}
                        {relatedData && (relatedData as any[]).length > 3 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    All Related Recipes
                                </Typography>
                                <Grid container spacing={2}>
                                    {(relatedData as any[]).map((recipe: any, index: number) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                backgroundColor: '#f8fafc',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <Avatar
                                                    src={recipe.recipeImage}
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: '600' }}>
                                                        {recipe.recipeName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ID: {recipe.recipeId}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}